import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend, CartesianGrid } from "recharts";

const STATUS_COLORS = {
  "Open": "#00aaff",
  "Assigned": "#00aaff",
  "Work In Progress": "#f59e0b",
  "Pending Customer": "#a855f7",
  "Resolved": "#39ff14",
  "Ticket Closed": "#4b5563",
};

export default function AnalyticsTab() {
  const [scope, setScope] = useState("all");
  const [tix, setTix] = useState(null);
  const [users, setUsers] = useState(null);

  useEffect(() => {
    api.get(`/analytics/tickets`, { params: { scope } }).then((r) => setTix(r.data)).catch(() => {});
  }, [scope]);
  useEffect(() => {
    api.get(`/analytics/users`).then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-6" data-testid="analytics-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-lg font-semibold">Tickets &amp; Signups</h2>
        <div className="flex items-center gap-1">
          {["month", "quarter", "year", "all"].map((s) => (
            <button key={s} onClick={() => setScope(s)} className={`csc-pill ${scope === s ? "csc-pill-active" : ""}`} data-testid={`scope-${s}`}>{s}</button>
          ))}
        </div>
      </div>

      {tix && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            {Object.entries(STATUS_COLORS).map(([k, c]) => {
              const v = (tix.by_status || []).find((s) => s.status === k)?.count || 0;
              return (
                <div key={k} className="csc-card p-4">
                  <div className="font-mono-tech text-[10px] uppercase tracking-wider text-[#94a3b8]">{k}</div>
                  <div className="text-2xl font-semibold mt-1" style={{ color: c }}>{v}</div>
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="csc-card p-5">
              <h3 className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-[#94a3b8] mb-3">Tickets per month</h3>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={tix.by_month || []}>
                  <CartesianGrid strokeDasharray="2 2" stroke="rgba(0,170,255,0.18)" />
                  <XAxis dataKey="month" tick={{ fill: "#94a3b8", fontSize: 10 }} />
                  <YAxis allowDecimals={false} tick={{ fill: "#94a3b8", fontSize: 10 }} />
                  <Tooltip contentStyle={{ background: "#0d1b2e", border: "1px solid rgba(0,170,255,0.18)", color: "#fff" }} />
                  <Bar dataKey="count" fill="#00aaff" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="csc-card p-5">
              <h3 className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-[#94a3b8] mb-3">Tickets by status</h3>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={tix.by_status || []} dataKey="count" nameKey="status" outerRadius={90} stroke="#0d1b2e">
                    {(tix.by_status || []).map((entry, i) => (
                      <Cell key={i} fill={STATUS_COLORS[entry.status] || "#00aaff"} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#0d1b2e", border: "1px solid rgba(0,170,255,0.18)", color: "#fff" }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div className="csc-card p-4"><div className="font-mono-tech text-[10px] uppercase tracking-wider text-[#94a3b8]">All-time resolved</div><div className="text-2xl font-semibold mt-1 text-[#a78bfa]">{tix.all_time_resolved}</div></div>
            <div className="csc-card p-4"><div className="font-mono-tech text-[10px] uppercase tracking-wider text-[#94a3b8]">All-time closed</div><div className="text-2xl font-semibold mt-1 text-[#94a3b8]">{tix.all_time_closed}</div></div>
            <div className="csc-card p-4"><div className="font-mono-tech text-[10px] uppercase tracking-wider text-[#94a3b8]">Avg resolution (hrs)</div><div className="text-2xl font-semibold mt-1 text-[#39ff14]">{tix.avg_resolution_hours ?? "—"}</div></div>
          </div>
        </>
      )}

      {users && (
        <div className="csc-card p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-[#94a3b8]">Signups (last 12 months)</h3>
            <div className="font-mono-tech text-[11px] text-[#94a3b8]">People: <span className="text-white">{users.totals.people}</span> · Orgs: <span className="text-white">{users.totals.organizations}</span></div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={users.monthly || []}>
              <CartesianGrid strokeDasharray="2 2" stroke="rgba(0,170,255,0.18)" />
              <XAxis dataKey="month" tick={{ fill: "#94a3b8", fontSize: 10 }} />
              <YAxis allowDecimals={false} tick={{ fill: "#94a3b8", fontSize: 10 }} />
              <Tooltip contentStyle={{ background: "#0d1b2e", border: "1px solid rgba(0,170,255,0.18)", color: "#fff" }} />
              <Legend />
              <Line type="monotone" dataKey="people" stroke="#39ff14" strokeWidth={2} dot={{ fill: "#39ff14" }} />
              <Line type="monotone" dataKey="organizations" stroke="#00aaff" strokeWidth={2} dot={{ fill: "#00aaff" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
